import type { ReactNode } from "react";

/**
 * A titled block of settings.
 *
 * Two of these existed, one per settings page, with the same name and the same
 * props but different markup -- so the admin and personal pages framed the same
 * kind of content differently for no reason anybody had decided on.
 *
 * `collapsible` is the one real difference and stays a choice: the admin page
 * has nine of these and is a page you scan for the one you want, while the
 * personal page has seven short ones that are quicker to read open.
 *
 * The collapsed form is `<details>` rather than state and a conditional: it
 * gets the disclosure semantics, keyboard handling and find-in-page behaviour
 * for free, and the browser keeps the open state while the page is mounted.
 *
 * The title carries the section on its own. These used to take a sentence of
 * explanation as well, which made a page of nine sections into a page of
 * eighteen lines to read before you could find the one you came for -- and the
 * explanation is only wanted once, whereas the list is scanned every time.
 * Anything genuinely needed to use a setting belongs beside that setting.
 */
export function Panel({
  title,
  collapsible = false,
  children,
}: {
  title: string;
  collapsible?: boolean;
  children: ReactNode;
}) {
  const heading = (
    <div className="min-w-0">
      <h2 className="text-sm font-medium text-content">{title}</h2>
    </div>
  );

  if (!collapsible) {
    return (
      <section className="mb-4 rounded-lg border border-line bg-surface">
        <header className="border-b border-line px-4 py-3">{heading}</header>
        <div className="p-4">{children}</div>
      </section>
    );
  }

  return (
    <details className="group mb-3 rounded-lg border border-line bg-surface">
      <summary className="cursor-pointer list-none px-4 py-3 hover:bg-surface-2">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="shrink-0 text-xs text-faint transition-transform group-open:rotate-90"
          >
            ▶
          </span>
          {heading}
        </div>
      </summary>
      <div className="border-t border-line p-4">{children}</div>
    </details>
  );
}
